import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";

import getDecorators from "inversify-inject-decorators";
import { DefaultEventService } from "../../../services/event-service";
import { Events, SceneId } from "../../../shared/enum";
import { BattleVerticalMenu } from "../../components/base-menu";
import { BattleScene, Unit } from "../../scenes/battle/side-battle-scene";
import { GeometryService } from "../../ui-services/geometry-ui-service";
import { Message } from "../message-box";

const { lazyInject } = getDecorators(container);

export interface BattleMenu {
    draw(): void;

    bindEvents(): void;

    process(): void;

    signalReady(): void;
}

export class SideBattleMenu implements BattleMenu {
    @lazyInject(TYPES.GeometryService)
    private geometryService: GeometryService;

    @lazyInject(TYPES.EventService)
    private eventService: DefaultEventService;

    private _callingScene: Phaser.Scene;
    private battleScene: BattleScene;
    private message: Message;

    private heroesMenu: HeroesMenu;
    private enemiesMenu: EnemiesMenu;
    private actionsMenu: ActionsMenu;
    private currentMenu: BattleVerticalMenu;
    private inputBlocked: boolean = false;

    constructor(scene: Phaser.Scene) {
        this._callingScene = scene;
        this.battleScene = this.scene.scene.get(SceneId.BATTLE_SCENE) as BattleScene;
        this.inputBlocked = false;
        console.log("battle menu scene constructed");

        // Remove any stale registrations (same method reference, any context) before
        // adding fresh ones.  This prevents listener accumulation across battles if a
        // previous SideBattleMenu instance's cleanup() was somehow skipped.
        this.battleScene.events.off(Events.REFRESH_BATTLE_MENU, this.process);
        this.battleScene.events.on(Events.REFRESH_BATTLE_MENU, this.process, this);

        // Listen for input blocking events
        this.battleScene.events.off(Events.BLOCK_BATTLE_INPUT, this.blockInput);
        this.battleScene.events.on(Events.BLOCK_BATTLE_INPUT, this.blockInput, this);
        this.battleScene.events.off(Events.UNBLOCK_BATTLE_INPUT, this.unblockInput);
        this.battleScene.events.on(Events.UNBLOCK_BATTLE_INPUT, this.unblockInput, this);

        // Clean up listeners when BattleMenuScene shuts down to prevent ghost callbacks
        this.scene.events.on("shutdown", this.cleanup, this);
    }

    public draw() {
        // draw some background for the menu
        const graphics = this.scene.add.graphics();

        this.geometryService.initGraphicContext(graphics);

        const screenWidth = this.scene.physics.world.bounds.width;
        const screenHeight = this.scene.physics.world.bounds.height;

        const sideMenusWidth = (screenWidth * 35) / 100;
        const innerMenuWidth = (screenWidth * 30) / 100;

        this.geometryService.drawBox(2, screenHeight - 100, sideMenusWidth, 100);
        this.geometryService.drawBox(2 + sideMenusWidth, screenHeight - 100, innerMenuWidth, 100);
        this.geometryService.drawBox(
            2 + sideMenusWidth + innerMenuWidth,
            screenHeight - 100,
            sideMenusWidth,
            100
        );

        // basic container to hold all menus
        //@ts-ignore
        const menus = this.scene.add.container();

        this.heroesMenu = new HeroesMenu(
            8 + sideMenusWidth + innerMenuWidth,
            screenHeight - 93,
            this.scene
        );
        this.actionsMenu = new ActionsMenu(8 + sideMenusWidth, screenHeight - 93, this.scene);
        this.enemiesMenu = new EnemiesMenu(8, screenHeight - 93, this.scene);

        // the currently selected menu
        this.currentMenu = this.actionsMenu;

        // add menus to the container
        menus.add(this.heroesMenu);
        menus.add(this.actionsMenu);
        menus.add(this.enemiesMenu);

        // the message describing the current action
        this.message = new Message(this.scene, this.battleScene.events);
        this.scene.add.existing(this.message);
    }

    public bindEvents() {
        // listen for keyboard events
        this.scene.input.keyboard.on("keydown", this.onKeyInput, this);

        // when its player unit turn to move — remove stale first (see constructor comment)
        this.battleScene.events.off("PlayerSelect", this.onPlayerSelect);
        this.battleScene.events.on("PlayerSelect", this.onPlayerSelect, this);

        // when the action on the menu is selected
        // for now we have only one action so we dont send and action id
        this.scene.events.on("SelectedAction", this.onSelectedAction, this);

        // an enemy is selected
        this.scene.events.on("Enemy", this.onEnemy, this);

        // refresh request
        this.scene.events.on("Refresh", this.process, this);
    }

    public process() {
        console.log("calling process and remapping");
        // map hero menu items to heroes
        this.remapHeroes();
        // map enemies menu items to enemies
        this.remapEnemies();
    }

    /**
     * Signal to the server that the battle UI is fully ready.
     * Must be called AFTER bindEvents() and draw() to prevent race conditions.
     */
    public signalReady() {
        console.log("BattleMenu: Signaling battle_ready to server");
        this.eventService.emit(Events.BATTLE_READY, {});
    }

    /**
     * Clean up all listeners registered on battleScene.events.
     * Called when BattleMenuScene shuts down to prevent ghost callbacks on next battle.
     */
    private cleanup() {
        console.log("BattleMenu: Cleaning up listeners on battleScene.events");
        this.battleScene.events.off(Events.REFRESH_BATTLE_MENU, this.process, this);
        this.battleScene.events.off(Events.BLOCK_BATTLE_INPUT, this.blockInput, this);
        this.battleScene.events.off(Events.UNBLOCK_BATTLE_INPUT, this.unblockInput, this);
        this.battleScene.events.off("PlayerSelect", this.onPlayerSelect, this);
        this.scene.input.keyboard.off("keydown", this.onKeyInput, this);
        this.scene.events.off("shutdown", this.cleanup, this);
    }

    get scene() {
        return this._callingScene;
    }

    public onEnemy(index: number) {
        // when the enemy is selected, we deselect all menus and send event with the enemy id
        this.heroesMenu.deselect();
        this.actionsMenu.deselect();
        this.enemiesMenu.deselect();
        this.currentMenu = null;
        this.battleScene.events.emit(Events.ATTACK, "attack", index);
    }

    public onPlayerSelect(id: number) {
        // when its player turn, we select the active hero item and the first action
        // then we make actions menu active
        console.log("player select");
        console.log("when the bug is active, this was never called");
        console.log("trying to select id : " + id);
        this.heroesMenu.select(id);
        this.actionsMenu.select(0);
        this.currentMenu = this.actionsMenu;
    }

    // we have action selected and we make the enemies menu active
    // the player needs to choose an enemy to attack
    public onSelectedAction() {
        this.currentMenu = this.enemiesMenu;
        this.enemiesMenu.select(0);
    }

    public onKeyInput(event: KeyboardEvent) {
        // Block all input during message playback
        if (this.inputBlocked) {
            console.log("BattleMenu: Input blocked during message playback");
            return;
        }

        if (this.currentMenu && this.currentMenu.selected) {
            if (event.code === "ArrowUp") {
                this.currentMenu.moveSelectionUp();
            } else if (event.code === "ArrowDown") {
                this.currentMenu.moveSelectionDown();
            } else if (event.code === "ArrowRight" || event.code === "Shift") {
            } else if (event.code === "Space" || event.code === "ArrowLeft") {
                this.currentMenu.confirm();
            }
        }
    }

    private blockInput(): void {
        this.inputBlocked = true;
        // Deselect all menus for visual feedback
        this.currentMenu = null;
        this.heroesMenu.deselect();
        this.actionsMenu.deselect();
        this.enemiesMenu.deselect();
        console.log("BattleMenu: Battle input BLOCKED - displaying enemy turns");
    }

    private unblockInput(): void {
        this.inputBlocked = false;
        console.log("BattleMenu: Battle input UNBLOCKED - ready for player input");
        // Note: Don't re-select menu here; onPlayerInput will handle selection
    }

    public remap(menu: BattleVerticalMenu, units: Unit[]) {
        console.log("REMAP ", units);
        menu.clear();
        units.forEach((unit) => {
            const unitHp = unit.findStatById("hp");
            const unitMaxHp = unit.findStatById("max_hp");

            const text = unit.unitModel.alive
                ? unit.unitModel.name + "    " + unitHp.value + " / " + unitMaxHp.value
                : "";
            menu.addMenuItem(text);
        });

        menu.select(menu.menuItemIndex);
    }

    public remapHeroes() {
        const heroes = this.battleScene.heroes;

        this.remap(this.heroesMenu, heroes);
    }

    public remapEnemies() {
        const enemies = this.battleScene.enemies;
        //        console.log("this.battleScene.enemies: ", JSON.parse(JSON.stringify(enemies)));
        this.remap(this.enemiesMenu, enemies);
    }
}

class HeroesMenu extends BattleVerticalMenu {
    constructor(x: number, y: number, scene: Phaser.Scene) {
        super(x, y, scene);
    }

    public confirm() {}
}

class ActionsMenu extends BattleVerticalMenu {
    constructor(x: number, y: number, scene: Phaser.Scene) {
        super(x, y, scene);
        this.addMenuItem("Attack");
    }

    public confirm() {
        // we select an action and go to the next menu and choose from the enemies to apply the action
        this.scene.events.emit("SelectedAction");
    }
}

class EnemiesMenu extends BattleVerticalMenu {
    constructor(x: number, y: number, scene: Phaser.Scene) {
        super(x, y, scene);
    }

    public confirm() {
        // the player has selected the enemy and we send its id with the event
        this.scene.events.emit("Enemy", this.menuItemIndex);
    }
}
